/**
 * Gate BATON WebSocket connect/reconnect on a usable auth session (#132).
 * Prevents pre-auth 1006 storms and re-kicks BATON after sign-in victory.
 */

import { isSessionDead } from './sessionDead';

function decodeJwtExpMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const expSec = Number(payload?.exp || 0);
    if (!Number.isFinite(expSec) || expSec <= 0) return null;
    return expSec * 1000;
  } catch {
    return null;
  }
}

function readStoredAccessToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('maestro_auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { accessToken?: string };
    const token = parsed?.accessToken;
    return typeof token === 'string' && token.length >= 10 ? token : null;
  } catch {
    return null;
  }
}

/** True when BATON may open or retry a WebSocket (valid, non-expired access token + live session). */
export function canAttemptBatonConnect(nowMs = Date.now()): boolean {
  if (isSessionDead()) return false;
  try {
    if (typeof sessionStorage !== 'undefined'
      && sessionStorage.getItem('maestro_refresh_failed') === '1') {
      return false;
    }
  } catch { /* ignore */ }
  const token = readStoredAccessToken();
  if (!token) return false;
  const expMs = decodeJwtExpMs(token);
  if (expMs && expMs <= nowMs) return false;
  return true;
}

export function batonAuthConnectBlockedReason(): string | null {
  if (isSessionDead()) return 'session_dead';
  try {
    if (typeof sessionStorage !== 'undefined'
      && sessionStorage.getItem('maestro_refresh_failed') === '1') {
      return 'refresh_failed';
    }
  } catch { /* ignore */ }
  const token = readStoredAccessToken();
  if (!token) return 'no_access_token';
  const expMs = decodeJwtExpMs(token);
  if (expMs && expMs <= Date.now()) return 'access_token_expired';
  return null;
}

/** Dispatch after sign-in / session restore so BATON reconnects with fresh credentials. */
export function notifyBatonAuthReady(source: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('maestro:baton-auth-ready', { detail: { source } }));
  } catch { /* ignore */ }
}

export function resetBatonAuthGateForTests(): void {
  // sessionDead has its own reset; nothing module-local to clear
}
